import * as z from "zod/v4";

// Inlined TCR brand-fields schema. The canonical version lives in
// @redotech/redo-onboarding-api/schema/shared/tcr but admin/sdk can't
// depend on the onboarding-api package (would create a cross-package
// dependency). These enums must stay in lockstep — if the onboarding
// schema gains a new BusinessType or IndustryVertical, mirror it here.
const TcrBusinessTypeEnum = z.enum([
  "PRIVATE_PROFIT",
  "PUBLIC_PROFIT",
  "NON_PROFIT",
  "GOVERNMENT",
  "SOLE_PROPRIETOR",
]);
const TcrIndustryVerticalEnum = z.enum([
  "RETAIL",
  "ECOMMERCE",
  "FOOD_AND_DRINK",
  "FASHION",
  "HEALTH_AND_BEAUTY",
  "FITNESS",
  "HOBBIES",
  "HOME_AND_GARDEN",
  "OTHER",
]);
const TcrStockExchangeEnum = z.enum(["NYSE", "NASDAQ", "AMEX", "OTHER"]);

/**
 * Admin-initiated Recover 10DLC provisioning for a team that hasn't
 * self-served through the onboarding flow. The modal in the
 * registrations dashboard collects the same `rec-business-info` fields
 * a merchant would, optionally pre-populated from team settings, then
 * calls this RPC.
 *
 * Server-side: writes the form data to `onboarding_v2_progress.form_data`
 * so the Provisioning tab's submittedPayload view stays consistent with
 * the self-serve path, then delegates to the shared
 * `startRecoverProvisioning` helper.
 *
 * Bypasses the `RECOVER_PHONE_PROVISIONING_ONBOARDING` flag on purpose —
 * ops needs a manual path for merchants who won't self-serve. The
 * `requireExplicitConfirm` guard matches the retry/trigger RPCs: blocks
 * accidental curl without the admin UI's cost-disclosure modal.
 */
export const createRecoverProvisioningForTeam = {
  name: "createRecoverProvisioningForTeam" as const,
  input: z.object({
    teamId: z.string(),
    requireExplicitConfirm: z.boolean(),
    /**
     * Default true. When true, flips `settings.recover.useSharedNumber`
     * on after starting the workflow so SMS sends via Redo's shared
     * number during the multi-day verification window. Auto-clears on
     * Verified. Admin modal surfaces this as a checked-by-default box.
     */
    enableSharedNumberFallback: z.boolean().optional().default(true),
    businessInfo: z.object({
      legalEntityName: z.string().min(1).optional(),
      ein: z
        .string()
        .regex(/^\d{9}$/)
        .optional(),
      brandName: z.string().min(1).optional(),
      supportEmail: z.string().email().optional(),
      businessType: TcrBusinessTypeEnum.optional(),
      industryVertical: TcrIndustryVerticalEnum.optional(),
      contactJobTitle: z.string().min(1).optional(),
      isPubliclyTraded: z.boolean().optional(),
      stockSymbol: z.string().optional(),
      stockExchange: TcrStockExchangeEnum.optional(),
    }),
    /**
     * Admin-only domain override. When set, used as the website value
     * submitted to TCR in place of team._shopify.domain. Stored in the
     * brand registration's submitted_payload only — **never** written
     * back to team._shopify.domain or team.storeUrl. Leave undefined
     * to use the team's authoritative Shopify domain.
     */
    domainOverride: z.string().optional(),
    /**
     * Admin-supplied overrides for team-level profile fields. Any
     * field left undefined is left unchanged on the team document.
     */
    merchantProfile: z
      .object({
        street1: z.string().optional(),
        street2: z.string().optional(),
        city: z.string().optional(),
        state: z.string().optional(),
        zip: z.string().optional(),
        country: z.string().optional(),
        adminName: z.string().optional(),
        adminPhone: z.string().optional(),
      })
      .optional(),
  }),
  output: z.discriminatedUnion("status", [
    z.object({ status: z.literal("started"), workflowId: z.string() }),
    z.object({ status: z.literal("already-running"), workflowId: z.string() }),
    z.object({ status: z.literal("completed"), phoneNumber: z.string() }),
    z.object({
      status: z.literal("blocked"),
      reason: z.enum([
        "confirmation-required",
        "team-not-found",
        "country-unsupported",
        "missing-form-data",
        "missing-merchant-data",
        "bandwidth-unavailable",
        "provider-unavailable",
        "internal-error",
      ]),
      detail: z.string().optional(),
      missingFields: z.array(z.string()).optional(),
    }),
  ]),
};
